import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

const AdminDashboard = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [blockedSubmissions, setBlockedSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const existing = sessionStorage.getItem('adminToken') || '';
    if (existing) {
      setToken(existing);
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchTestimonials(token);
      fetchBlockedSubmissions(token);
    }
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        return setMessage({ type: 'error', text: data?.error || 'Login failed' });
      }

      if (!data?.token) {
        return setMessage({ type: 'error', text: 'Login failed (no token returned)' });
      }

      sessionStorage.setItem('adminToken', data.token);
      setPassword('');
      setToken(data.token);
      return;
    } catch (error) {
      return setMessage({ type: 'error', text: 'Failed to connect to server' });
    }
  };

  const fetchTestimonials = async (activeToken) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/testimonials`, {
        headers: {
          Authorization: `Bearer ${activeToken}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setTestimonials(data);
        setMessage({ type: '', text: '' });
      } else if (response.status === 401 || response.status === 403) {
        sessionStorage.removeItem('adminToken');
        setToken('');
        setMessage({ type: 'error', text: 'Session expired. Please log in again.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load testimonials' });
    } finally {
      setLoading(false);
    }
  };

  const fetchBlockedSubmissions = async (activeToken) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/blocked-submissions`, {
        headers: {
          Authorization: `Bearer ${activeToken}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setBlockedSubmissions(data);
      }
    } catch (error) {
      console.error('Failed to load blocked submissions:', error);
    }
  };

  const approveTestimonial = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/testimonials/${id}/approve`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Testimonial approved!' });
        fetchTestimonials(token);
      } else if (response.status === 401 || response.status === 403) {
        sessionStorage.removeItem('adminToken');
        setToken('');
        setMessage({ type: 'error', text: 'Session expired. Please log in again.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to approve testimonial' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to connect to server' });
    }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/testimonials/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Testimonial deleted' });
        fetchTestimonials(token);
      } else if (response.status === 401 || response.status === 403) {
        sessionStorage.removeItem('adminToken');
        setToken('');
        setMessage({ type: 'error', text: 'Session expired. Please log in again.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to delete testimonial' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to connect to server' });
    }
  };

  const approveBlockedSubmission = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/blocked-submissions/${id}/approve`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Blocked submission approved and moved to pending!' });
        fetchBlockedSubmissions(token);
        fetchTestimonials(token);
      } else if (response.status === 401 || response.status === 403) {
        sessionStorage.removeItem('adminToken');
        setToken('');
        setMessage({ type: 'error', text: 'Session expired. Please log in again.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to approve blocked submission' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to connect to server' });
    }
  };

  const deleteBlockedSubmission = async (id) => {
    if (!window.confirm('Are you sure you want to permanently delete this blocked submission?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/blocked-submissions/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Blocked submission deleted permanently' });
        fetchBlockedSubmissions(token);
      } else if (response.status === 401 || response.status === 403) {
        sessionStorage.removeItem('adminToken');
        setToken('');
        setMessage({ type: 'error', text: 'Session expired. Please log in again.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to delete blocked submission' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to connect to server' });
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy to-slate-900 flex items-center justify-center px-6">
        <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Login</h1>
            <p className="text-gray-600">Enter password to access dashboard</p>
          </div>

          {message.text && (
            <div className={`mb-6 p-4 rounded-lg ${
              message.type === 'error' 
                ? 'bg-red-100 text-red-800 border border-red-200' 
                : 'bg-green-100 text-green-800 border border-green-200'
            }`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan focus:border-transparent mb-4"
              required
            />
            <button
              type="submit"
              className="w-full bg-cyan hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition-all"
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  const pendingTestimonials = testimonials.filter(t => !t.approved);
  const approvedTestimonials = testimonials.filter(t => t.approved);

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900">Testimonial Dashboard</h1>
          <button
            onClick={() => {
              sessionStorage.removeItem('adminToken');
              setToken('');
              setTestimonials([]);
              setMessage({ type: '', text: '' });
              setLoading(false);
            }}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-semibold transition-all"
          >
            Logout
          </button>
        </div>

        {loading && (
          <div className="mb-6 text-gray-600">Loading...</div>
        )}

        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'error' 
              ? 'bg-red-100 text-red-800 border border-red-200' 
              : 'bg-green-100 text-green-800 border border-green-200'
          }`}>
            {message.text}
          </div>
        )}

        {blockedSubmissions.length > 0 && (
          <div className="mb-8 bg-red-50 border-2 border-red-200 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-red-900 mb-4">
              ⚠️ Blocked Submissions ({blockedSubmissions.length})
            </h2>
            <p className="text-sm text-red-700 mb-4">
              These submissions were blocked by security filters. Review them to ensure no legitimate testimonials were caught.
            </p>
            <div className="space-y-4">
              {blockedSubmissions.map((submission) => (
                <div key={submission.id} className="bg-white rounded-xl p-6 shadow border-2 border-red-300">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center">
                        <span className="text-lg font-bold text-white">⚠️</span>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">{submission.name}</p>
                        <p className="text-sm text-gray-600">{submission.position} • {submission.school}</p>
                      </div>
                    </div>
                    <span className="bg-red-100 text-red-800 text-xs font-semibold px-3 py-1 rounded-full">
                      {submission.reason}
                    </span>
                  </div>
                  <p className="text-gray-700 italic mb-2">"{submission.text}"</p>
                  <p className="text-xs text-gray-500 mb-4">
                    Blocked: {new Date(submission.blockedAt).toLocaleString()} • IP: {submission.ip}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => approveBlockedSubmission(submission.id)}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-semibold transition-all"
                    >
                      ✓ Approve Anyway
                    </button>
                    <button
                      onClick={() => deleteBlockedSubmission(submission.id)}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold transition-all"
                    >
                      ✕ Delete Forever
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Pending Approval ({pendingTestimonials.length})
            </h2>
            {pendingTestimonials.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center text-gray-500">
                No pending testimonials
              </div>
            ) : (
              <div className="space-y-4">
                {pendingTestimonials.map((testimonial) => (
                  <div key={testimonial.id} className="bg-white rounded-xl p-6 shadow-lg border-2 border-orange-200">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center">
                          <span className="text-lg font-bold text-white">{testimonial.initials}</span>
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">{testimonial.name}</p>
                          <p className="text-sm text-gray-600">{testimonial.position} • {testimonial.school}</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-700 italic mb-4">"{testimonial.text}"</p>
                    <p className="text-xs text-gray-500 mb-4">
                      Submitted: {new Date(testimonial.createdAt).toLocaleString()}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => approveTestimonial(testimonial.id)}
                        className="flex-1 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-semibold transition-all"
                      >
                        ✓ Approve
                      </button>
                      <button
                        onClick={() => deleteTestimonial(testimonial.id)}
                        className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold transition-all"
                      >
                        ✕ Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Approved ({approvedTestimonials.length})
            </h2>
            <div className="space-y-4">
              {approvedTestimonials.map((testimonial) => (
                <div key={testimonial.id} className="bg-white rounded-xl p-6 shadow border-2 border-green-200">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-cyan to-cyan-600 rounded-full flex items-center justify-center">
                        <span className="text-lg font-bold text-white">{testimonial.initials}</span>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">{testimonial.name}</p>
                        <p className="text-sm text-gray-600">{testimonial.position} • {testimonial.school}</p>
                      </div>
                    </div>
                    <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
                      Live
                    </span>
                  </div>
                  <p className="text-gray-700 italic mb-4">"{testimonial.text}"</p>
                  <button
                    onClick={() => deleteTestimonial(testimonial.id)}
                    className="text-red-600 hover:text-red-800 text-sm font-semibold transition-all"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
